import { isCommentInvoiceLine } from "./commentInvoiceLines";
import { normalizeJobFlagValue } from "./jobFlags";

const JOB_DETAIL_FIELDS = [
	["name", "Name"],
	["description", "Description"],
	["instructions", "Instructions"],
	["notes", "Notes"],
	["parts", "Private Notes (legacy)"]
];

function getTrimmedString(value) {
	if (value === undefined || value === null) {
		return "";
	}
	if (Array.isArray(value)) {
		return value
			.map(entry => getTrimmedString(entry))
			.filter(Boolean)
			.join(", ");
	}
	if (typeof value === "object") {
		try {
			return JSON.stringify(value);
		} catch (err) {
			return "";
		}
	}
	return String(value).trim();
}

function getFiniteNumber(value, fallback) {
	if (value === undefined || value === null || (typeof value === "string" && value.trim() === "")) {
		return fallback;
	}
	const numberValue = Number(value);
	return Number.isFinite(numberValue) ? numberValue : fallback;
}

function roundCurrency(value) {
	return Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;
}

function isValidEstimate(estimate) {
	if (!estimate || typeof estimate !== "object" || Array.isArray(estimate)) {
		return false;
	}

	// selectedValue is the persisted pointer used by both pricing and job tasks.
	// Do not present an option that cannot be selected reliably.
	return getTrimmedString(estimate.value) !== "";
}

function getValidEstimates(source = {}) {
	return Array.isArray(source && source.estimates)
		? source.estimates.filter(isValidEstimate)
		: [];
}

function getActivePricingContractDocument(source = {}) {
	const pricingContractProto = source && source.pricingContractProto;
	const documents = pricingContractProto && Array.isArray(pricingContractProto.documents)
		? pricingContractProto.documents
		: [];
	if (documents.length === 0) {
		return null;
	}

	return documents.find(document => (
		document && getTrimmedString(document.id) === getTrimmedString(pricingContractProto.activeDocumentId)
	)) || documents[documents.length - 1] || null;
}

export function hasActivePricingContract(source = {}) {
	const activeDocument = getActivePricingContractDocument(source);
	return Boolean(activeDocument && Array.isArray(activeDocument.invoiceItems) && activeDocument.invoiceItems.length > 0);
}

function normalizeEstimateLockActor(actor) {
	const source = actor && typeof actor === "object" && !Array.isArray(actor)
		? actor
		: {};

	return {
		identityId: getTrimmedString(source.identityId),
		displayName: getTrimmedString(source.displayName),
		username: getTrimmedString(source.username)
	};
}

export function normalizeEstimateLock(estimateLock) {
	if (!estimateLock || typeof estimateLock !== "object" || Array.isArray(estimateLock)) {
		return null;
	}

	const estimateValue = getTrimmedString(estimateLock.estimateValue);
	if (!estimateValue) {
		return null;
	}

	const explicitStatus = getTrimmedString(estimateLock.status).toLowerCase();
	const status = explicitStatus === "locked" || explicitStatus === "unlocked"
		? explicitStatus
		: (estimateLock.locked === true ? "locked" : "");
	if (!status) {
		return null;
	}

	const normalizedLock = {
		schemaVersion: 1,
		status,
		estimateValue,
		estimateName: getTrimmedString(estimateLock.estimateName),
		lockedAt: getTrimmedString(estimateLock.lockedAt),
		lockedBy: normalizeEstimateLockActor(estimateLock.lockedBy)
	};

	if (status === "unlocked") {
		normalizedLock.unlockedAt = getTrimmedString(estimateLock.unlockedAt);
		normalizedLock.unlockedBy = normalizeEstimateLockActor(estimateLock.unlockedBy);
	}

	return normalizedLock;
}

export function getEstimateLock(source = {}) {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return null;
	}

	return normalizeEstimateLock(source.estimateLock);
}

export function isEstimateLocked(source = {}) {
	const estimateLock = getEstimateLock(source);
	return Boolean(estimateLock && estimateLock.status === "locked");
}

export function normalizeEstimateApproval(estimateApproval) {
	if (!estimateApproval || typeof estimateApproval !== "object" || Array.isArray(estimateApproval)) {
		return null;
	}

	const estimateValue = getTrimmedString(estimateApproval.estimateValue);
	if (!estimateValue) {
		return null;
	}

	const explicitStatus = getTrimmedString(estimateApproval.status).toLowerCase();
	const status = explicitStatus === "approved" || explicitStatus === "confirmed"
		? "approved"
		: (estimateApproval.approved === true ? "approved" : "");
	if (!status) {
		return null;
	}

	return {
		...estimateApproval,
		schemaVersion: 1,
		status,
		estimateValue,
		estimateName: getTrimmedString(estimateApproval.estimateName),
		approvedEstimateId: getTrimmedString(estimateApproval.approvedEstimateId),
		approvedEstimateValue: getTrimmedString(estimateApproval.approvedEstimateValue),
		approvedEstimateName: getTrimmedString(estimateApproval.approvedEstimateName),
		approvedAt: getTrimmedString(estimateApproval.approvedAt),
		approvedBy: normalizeEstimateLockActor(estimateApproval.approvedBy)
	};
}

export function getEstimateApproval(source = {}) {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return null;
	}

	return normalizeEstimateApproval(source.estimateApproval);
}

export function getEstimateApprovalHistory(source = {}) {
	const approvals = Array.isArray(source && source.estimateApprovals)
		? source.estimateApprovals
		: [];

	return approvals
		.map(normalizeEstimateApproval)
		.filter(Boolean);
}

export function isEstimateConfirmed(source = {}) {
	return isEstimateLocked(source) || Boolean(getEstimateApproval(source));
}

export function isEstimateApproved(source = {}) {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return false;
	}

	const indexedApprovalStatus = getTrimmedString(source.estimateApprovalStatus).toLowerCase();
	return isEstimateConfirmed(source)
		|| normalizeJobFlagValue(source.estimateApproved)
		|| indexedApprovalStatus === "approved"
		|| indexedApprovalStatus === "confirmed";
}

export function getEstimatePrintOptions(estimates = [], estimateValue = "") {
	const sourceEstimates = Array.isArray(estimates) ? estimates : [];
	const requestedValue = getTrimmedString(estimateValue);
	if (!requestedValue) {
		return sourceEstimates;
	}

	return sourceEstimates.filter(estimate => (
		isValidEstimate(estimate)
		&& getTrimmedString(estimate.value) === requestedValue
	));
}

function isEstimateJobFlag(flag) {
	if (flag && typeof flag === "object" && !Array.isArray(flag)) {
		return isEstimateJobFlag(flag.value)
			|| isEstimateJobFlag(flag.label)
			|| isEstimateJobFlag(flag.name);
	}

	const normalizedFlag = getTrimmedString(flag)
		.toLowerCase()
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ");
	return normalizedFlag === "estimate" || normalizedFlag === "estimate needed";
}

function hasEstimateJobFlag(jobFlags) {
	const flags = Array.isArray(jobFlags) ? jobFlags : [jobFlags];
	return flags.some(isEstimateJobFlag);
}

function getNestedEstimateRequest(children, result = { found: false, requested: false }) {
	if (!Array.isArray(children)) {
		return result;
	}

	children.forEach((item) => {
		if (!item || typeof item !== "object") {
			return;
		}
		if (getTrimmedString(item.id).toLowerCase() === "ccestimateyesno") {
			result.found = true;
			result.requested = result.requested || normalizeJobFlagValue(item.value);
		}
		getNestedEstimateRequest(item.children, result);
	});

	return result;
}

export function isEstimateRequested(source = {}) {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return false;
	}

	// The hydrated activity item is canonical. Its explicit No must override
	// stale top-level/indexed metadata, matching the search indexer's resolver.
	const nestedRequest = getNestedEstimateRequest(source.children);
	if (nestedRequest.found) {
		return nestedRequest.requested;
	}

	const directSignalKeys = ["ccEstimateYesNo", "estimateNeeded", "estimateRequested"];
	for (let index = 0; index < directSignalKeys.length; index++) {
		const key = directSignalKeys[index];
		if (Object.prototype.hasOwnProperty.call(source, key)) {
			return normalizeJobFlagValue(source[key]);
		}
	}

	if (source.variables && Object.prototype.hasOwnProperty.call(source.variables, "ccEstimateYesNo")) {
		const variable = source.variables.ccEstimateYesNo;
		const variableValue = variable && typeof variable === "object" && !Array.isArray(variable)
			? variable.value
			: variable;
		return normalizeJobFlagValue(variableValue);
	}

	return hasEstimateJobFlag(source.jobFlags);
}

function isCheckedSummaryItem(item = {}) {
	return item.checked === true
		|| item.checked === 1
		|| getTrimmedString(item.checked).toLowerCase() === "true";
}

function getSelectionValue(selection) {
	if (selection && typeof selection === "object" && !Array.isArray(selection)) {
		if (Object.prototype.hasOwnProperty.call(selection, "value")) {
			return selection.value;
		}
		if (Object.prototype.hasOwnProperty.call(selection, "id")) {
			return selection.id;
		}
	}
	return selection;
}

function getSelectionLabel(options, selection) {
	const selectionValue = getSelectionValue(selection);
	const normalizedSelectionValue = getTrimmedString(selectionValue);
	const option = (Array.isArray(options) ? options : []).find((candidate) => {
		if (!candidate || typeof candidate !== "object") {
			return false;
		}
		return getTrimmedString(candidate.value) === normalizedSelectionValue;
	});

	if (option) {
		return getTrimmedString(option.label)
			|| getTrimmedString(option.name)
			|| getTrimmedString(option.title)
			|| normalizedSelectionValue;
	}

	if (selection && typeof selection === "object" && !Array.isArray(selection)) {
		return getTrimmedString(selection.label)
			|| getTrimmedString(selection.name)
			|| normalizedSelectionValue;
	}

	return normalizedSelectionValue;
}

function getActivitySummaryItemValue(item = {}) {
	const type = getTrimmedString(item.type).toLowerCase();

	if (type === "itemyesno") {
		const yesNoValue = getTrimmedString(item.value).toLowerCase();
		if (yesNoValue === "no" || yesNoValue === "false" || item.value === false || item.value === 0) {
			return "No";
		}
		if (yesNoValue === "yes" || yesNoValue === "true" || item.value === true || item.value === 1) {
			return "Yes";
		}
		return getTrimmedString(item.value);
	}

	if (type === "itemchoicesingle") {
		return getSelectionLabel(item.choiceItems, item.value);
	}

	if (type === "itempicker" || type.indexOf("itempicker") === 0) {
		return getSelectionLabel(item.pickerItems, item.value);
	}

	if (type === "itemchoicemulti") {
		const selectedValues = Array.isArray(item.value) ? item.value : [item.value];
		return selectedValues
			.map(selection => getSelectionLabel(item.choiceItems, selection))
			.filter(Boolean)
			.join(", ");
	}

	return getTrimmedString(item.value);
}

export function getEstimateCount(source = {}) {
	const explicitCount = source && source.estimateCount;
	if (typeof explicitCount === "number" && Number.isFinite(explicitCount) && explicitCount >= 0) {
		return Math.floor(explicitCount);
	}

	const estimates = getValidEstimates(source);
	if (isEstimateRequested(source)) {
		if (estimates.length > 0) {
			return estimates.length;
		}

		const hasLegacyInvoiceItems = Array.isArray(source && source.invoiceItems)
			&& source.invoiceItems.length > 0;
		const legacyInvoiceItemCount = source && source.invoiceItemCount;
		const hasIndexedLegacyItems = typeof legacyInvoiceItemCount === "number"
			&& Number.isFinite(legacyInvoiceItemCount)
			&& legacyInvoiceItemCount > 0;
		return hasLegacyInvoiceItems || hasIndexedLegacyItems ? 1 : 0;
	}

	const hasNonOriginalEstimate = estimates.some(estimate => (
		getTrimmedString(estimate && estimate.value).toLowerCase() !== "original"
	));
	if (estimates.length > 1 || hasNonOriginalEstimate) {
		return estimates.length;
	}

	// Root invoiceItems represent the active price quote, not an estimate by
	// themselves. A legacy Original is likewise hidden unless estimates were
	// requested or another named estimate exists.
	return 0;
}

export function hasEstimateLifecycle(source = {}) {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return false;
	}

	return isEstimateApproved(source)
		|| isEstimateRequested(source)
		|| getEstimateCount(source) > 0;
}

export function getEstimateDisplayName(estimate = {}, index = 0) {
	const preferredName = [
		estimate && estimate.jobDetails && estimate.jobDetails.name,
		estimate && estimate.name,
		estimate && estimate.label,
		estimate && estimate.title
	].map(getTrimmedString).find(Boolean);

	if (preferredName) {
		return preferredName;
	}

	const estimateValue = getTrimmedString(estimate && estimate.value);
	if (estimateValue.toLowerCase() === "original") {
		return "Original";
	}
	const generatedEstimateMatch = estimateValue.match(/^estimate-(\d+)$/i);
	if (generatedEstimateMatch) {
		return `Estimate ${Number(generatedEstimateMatch[1])}`;
	}
	if (estimateValue) {
		return estimateValue;
	}

	const normalizedIndex = Number.isFinite(Number(index)) && Number(index) >= 0
		? Math.floor(Number(index))
		: 0;
	return `Estimate ${normalizedIndex + 1}`;
}

export function getEstimateLineTotal(item = {}) {
	if (isCommentInvoiceLine(item)) return 0;
	const explicitValue = getFiniteNumber(item && item.value, null);
	if (explicitValue !== null) {
		return roundCurrency(explicitValue);
	}

	const qty = getFiniteNumber(item && item.qty, 0);
	const rate = getFiniteNumber(item && item.rate, 0);
	const discount = getFiniteNumber(item && item.discount, 1);
	return roundCurrency(qty * rate * discount);
}

export function getEstimateTotal(estimate = {}) {
	const invoiceItems = Array.isArray(estimate && estimate.invoiceItems) ? estimate.invoiceItems : [];
	return roundCurrency(invoiceItems.reduce((total, item) => total + getEstimateLineTotal(item), 0));
}

export function getEstimateOptions(checklist = {}) {
	const activeContractDocument = getActivePricingContractDocument(checklist);
	if (activeContractDocument && Array.isArray(activeContractDocument.invoiceItems) && activeContractDocument.invoiceItems.length > 0) {
		const contractJobDetails = activeContractDocument.jobDetails && typeof activeContractDocument.jobDetails === "object"
			? activeContractDocument.jobDetails
			: null;
		return [{
			name: getTrimmedString(activeContractDocument.title)
				|| getTrimmedString(activeContractDocument.name)
				|| "Original",
			value: "original",
			invoiceItems: activeContractDocument.invoiceItems,
			jobDetails: contractJobDetails || (
				checklist.jobDetails && typeof checklist.jobDetails === "object"
					? checklist.jobDetails
					: {}
			)
		}];
	}

	const estimates = getValidEstimates(checklist);
	if (estimates.length > 0) {
		return estimates;
	}

	const invoiceItems = Array.isArray(checklist && checklist.invoiceItems)
		? checklist.invoiceItems
		: [];
	const jobDetails = checklist.jobDetails && typeof checklist.jobDetails === "object"
		? checklist.jobDetails
		: {};

	if (invoiceItems.length === 0) {
		return [];
	}

	return [{
		name: "Original",
		value: "original",
		invoiceItems,
		jobDetails
	}];
}

export function hasJobPricing(source = {}) {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return false;
	}

	if (getEstimateCount(source) > 0) {
		return true;
	}
	if (Array.isArray(source.invoiceItems) && source.invoiceItems.length > 0) {
		return true;
	}
	if (getFiniteNumber(source.invoiceItemCount, 0) > 0) {
		return true;
	}
	if (getValidEstimates(source).some(estimate => (
		Array.isArray(estimate.invoiceItems) && estimate.invoiceItems.length > 0
	))) {
		return true;
	}

	const contractDocuments = source.pricingContractProto && Array.isArray(source.pricingContractProto.documents)
		? source.pricingContractProto.documents
		: [];
	if (contractDocuments.some(document => Array.isArray(document?.invoiceItems) && document.invoiceItems.length > 0)) {
		return true;
	}

	if (getFiniteNumber(source.totalPrice, 0) > 0) {
		return true;
	}
	const parsedTotal = Number(getTrimmedString(source.totalPriceString).replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsedTotal) && parsedTotal > 0;
}

export function getJobDetailsClipboardText(jobDetails = {}) {
	if (!jobDetails || typeof jobDetails !== "object" || Array.isArray(jobDetails)) {
		return "";
	}

	return JOB_DETAIL_FIELDS.map(([field, label]) => {
		const value = getTrimmedString(jobDetails[field]);
		return value ? `${label}: ${value}` : "";
	}).filter(Boolean).join("\n\n");
}

function activityItemIdMatches(candidateId, requestedId) {
	const normalizedCandidate = getTrimmedString(candidateId).toLowerCase();
	const normalizedRequested = getTrimmedString(requestedId).toLowerCase();
	return Boolean(normalizedCandidate && normalizedRequested && (
		normalizedCandidate === normalizedRequested
		|| normalizedCandidate.indexOf(`${normalizedRequested}_`) === 0
	));
}

function getStoredActivityItemValue(value) {
	if (value && typeof value === "object" && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, "value")) {
		return getTrimmedString(value.value);
	}
	return getTrimmedString(value);
}

function getActivityPropertyValueById(source = {}, itemId = "") {
	if (!source || typeof source !== "object" || Array.isArray(source)) {
		return "";
	}

	const matchingKey = Object.keys(source).find(key => activityItemIdMatches(key, itemId));
	return matchingKey ? getStoredActivityItemValue(source[matchingKey]) : "";
}

export function getActivityItemValueById(checklist = {}, itemId = "") {
	if (!checklist || typeof checklist !== "object" || Array.isArray(checklist) || !getTrimmedString(itemId)) {
		return "";
	}

	let resolvedValue = getActivityPropertyValueById(checklist, itemId)
		|| getActivityPropertyValueById(checklist.variables, itemId);
	function visit(nodes) {
		if (!Array.isArray(nodes) || resolvedValue) {
			return;
		}

		for (let index = 0; index < nodes.length && !resolvedValue; index += 1) {
			const node = nodes[index];
			if (!node || typeof node !== "object") {
				continue;
			}

			if (activityItemIdMatches(node.id, itemId)) {
				resolvedValue = getStoredActivityItemValue(node.value);
			}
			visit(node.children);
		}
	}

	visit(checklist.children);
	return resolvedValue;
}

export function getActivitySummaryRows(checklist = {}) {
	const rows = [];

	function visit(nodes, parentPath = []) {
		if (!Array.isArray(nodes)) {
			return;
		}

		nodes.forEach((node, index) => {
			if (!node || typeof node !== "object") {
				return;
			}

			const nodePath = parentPath.concat(index);
			const type = getTrimmedString(node.type).toLowerCase();
			const isItem = type.indexOf("item") === 0;
			const isExcludedMediaItem = type === "itemimagepicker" || type === "itemsketchpad";

			if (isItem && !isExcludedMediaItem && isCheckedSummaryItem(node)) {
				const value = getActivitySummaryItemValue(node);
				if (value) {
					const id = getTrimmedString(node.id)
						|| getTrimmedString(node.guid)
						|| `item-${nodePath.join("-")}`;
					const label = getTrimmedString(node.label1)
						|| getTrimmedString(node.label2)
						|| getTrimmedString(node.name)
						|| id;
					rows.push({ id, label, value });
				}
			}

			if (Array.isArray(node.children)) {
				visit(node.children, nodePath);
			}
		});
	}

	visit(checklist && checklist.children);
	return rows;
}

export function getActivitySummaryClipboardText(checklist = {}) {
	return getActivitySummaryRows(checklist)
		.map(row => `${row.label}: ${row.value}`)
		.join("\n\n");
}

export function hasInvoiceTaskProgress(items = []) {
	if (!Array.isArray(items)) {
		return false;
	}

	return items.some((item) => {
		if (!item || typeof item !== "object") {
			return false;
		}

		const checked = item.checked === true
			|| item.checked === 1
			|| getTrimmedString(item.checked).toLowerCase() === "true";
		const taskTime = getFiniteNumber(item.taskTime, 0);
		const hasTaskTimeEntry = getTrimmedString(item.taskTimeEnteredAt) !== "";
		const hasAssignment = getTrimmedString(item.identityId) !== "";
		return checked || taskTime > 0 || hasTaskTimeEntry || hasAssignment;
	});
}
